import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { OrbPlanPrice, OrbSubscription } from '../../../../../../typings/Orb.interface';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import BarChart from '../../../../../common/Chart/BarChart';
import { ChartDatapoint, ChartDataSet } from '../../../../../common/Chart/Chart';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { DashboardContext } from '../../../DashboardContext';
import TextSwitch from '../../../../../common/base/TextSwitch';
import { formatDate } from '../../../../../../utils/date';
import { useDatePreferences } from '../../../../../hooks/useDatePreferences';
const EventsUsage: React.FC<{
  subscription_details: OrbSubscription;
  product: 'event_gateway' | 'outpost';
}> = t0 => {
  const $ = _c(72);
  const {
    subscription_details,
    product
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription
  } = useContext(DashboardContext);
  const [active_meter_index, setActiveMeter] = useState(0);
  let t1;
  if ($[0] !== product || $[1] !== subscription_details) {
    t1 = subscription_details && APIMethodKeys.billing.getUsage({
      granularity: "day",
      timeframe_start: subscription_details.current_billing_period_start_date,
      timeframe_end: subscription_details.current_billing_period_end_date,
      product
    });
    $[0] = product;
    $[1] = subscription_details;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== HookdeckAPI.billing || $[4] !== product || $[5] !== subscription_details?.current_billing_period_end_date || $[6] !== subscription_details?.current_billing_period_start_date) {
    t2 = () => HookdeckAPI.billing.getUsage({
      granularity: "day",
      timeframe_start: subscription_details?.current_billing_period_start_date,
      timeframe_end: subscription_details?.current_billing_period_end_date,
      product
    });
    $[3] = HookdeckAPI.billing;
    $[4] = product;
    $[5] = subscription_details?.current_billing_period_end_date;
    $[6] = subscription_details?.current_billing_period_start_date;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: usage_data
  } = useSWR(t1, t2);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== active_meter_index || $[9] !== subscription?.max_requests || $[10] !== subscription_details || $[11] !== timezone || $[12] !== usage_data) {
    const meters = subscription_details?.plan.prices.reduce((acc, price) => {
      const is_same_plan_phase = !subscription_details?.plan.active_plan_phase_order || subscription_details.plan.active_plan_phase_order === price.plan_phase_order;
      if (price.price_type === "usage_price" && is_same_plan_phase) {
        return {
          ...acc,
          [price.id]: price
        };
      }
      return acc;
    }, {});
    const calculateTieredOverage = _temp;
    const meters_usage = [];
    if (usage_data && subscription_details) {
      for (const [, price_0] of Object.entries(meters)) {
        const daily_usage = usage_data.data.find(entry => entry.billable_metric.id === price_0.billable_metric.id)?.usage || [];
        const total_usage_0 = daily_usage.reduce(_temp2, 0);
        let included;
        let overage;
        let batch_size;
        let batch_price;
        if (price_0.tiered_package_config) {
          const tiered_config_0 = price_0.tiered_package_config;
          included = Number(tiered_config_0.tiers[1]?.tier_lower_bound || 0);
          batch_size = Number(tiered_config_0.package_size);
          batch_price = Number(tiered_config_0.tiers[1]?.per_unit || 0);
          overage = calculateTieredOverage(total_usage_0, tiered_config_0);
        } else {
          included = Number(price_0.package_with_allocation_config?.allocation || subscription?.max_requests || 0);
          const package_config = price_0.package_with_allocation_config || price_0.package_config;
          batch_size = Number(package_config?.package_size || 0);
          batch_price = Number(package_config?.package_amount || 0);
          if (price_0.discount?.percentage_discount) {
            batch_price = batch_price * price_0.discount?.percentage_discount;
          }
          const overage_batch_count = batch_size !== 0 ? Math.ceil((total_usage_0 - included) / batch_size) : 0;
          overage = overage_batch_count > 0 ? overage_batch_count * batch_price / 100 : 0;
        }
        const overage_ratio = total_usage_0 <= included ? 0 : 1 - included / total_usage_0;
        const base_ratio = overage_ratio === 0 ? total_usage_0 / included : 1 - overage_ratio;
        meters_usage.push({
          display_name: price_0.name,
          included,
          overage,
          batch_price,
          base_ratio,
          overage_ratio,
          daily_usage,
          total_usage: total_usage_0,
          batch_size
        });
      }
    }
    const active_meter = meters_usage[active_meter_index] || meters_usage[0];
    const chart_props = {
      title: "Usage",
      subtitle: "/ Periodic",
      legend_placeholder: "Usage",
      datasets: active_meter ? [{
        key: active_meter.display_name,
        label: active_meter.display_name,
        data: active_meter.daily_usage.map(usage => ({
          x: formatDate(usage.timeframe_start, "MM/dd/yyyy", timezone),
          y: usage.quantity
        })) as ChartDatapoint[],
        total: active_meter.total_usage,
        average: active_meter.total_usage / active_meter.daily_usage.length,
        metric_type: "count",
        theme_color: "primary",
        getDataLabel: _temp4
      }] as ChartDataSet[] : [],
      loading: meters_usage.length === 0,
      height: 316
    };
    T4 = Div;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = {
        b: 10
      };
      t14 = <Text bold={true} size="l" as="h2" m={0}>Metered usage</Text>;
      $[32] = t13;
      $[33] = t14;
    } else {
      t13 = $[32];
      t14 = $[33];
    }
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Text muted={true} as="p" m={{
        b: 4
      }}>Updated hourly</Text>;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    t16 = <StyledCard><BarChart {...chart_props} action={meters_usage.length > 1 ? <TextSwitch active={active_meter.display_name} options={meters_usage.map(_temp5)} onSelect={value_0 => {
        setActiveMeter(meters_usage.findIndex(meter_0 => meter_0.display_name === value_0));
      }} /> : undefined} /></StyledCard>;
    T3 = Div;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        t: 6
      };
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    T2 = StyledCard;
    let t17;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <GridUnit size={0.25}><Text muted={true} semi={true} size="xs" p={{
          x: 4,
          y: 1
        }} style={{
          lineHeight: "20px"
        }}>Type</Text></GridUnit>;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    let t18;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <GridUnit size={0.25}><Text muted={true} semi={true} size="xs" p={{
          x: 4,
          y: 1
        }} style={{
          lineHeight: "20px"
        }}>Usage</Text></GridUnit>;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    let t19;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <GridUnit size={0.25}><Text muted={true} semi={true} size="xs" p={{
          x: 4,
          y: 1
        }} style={{
          lineHeight: "20px"
        }}>Included with plan</Text></GridUnit>;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    let t20;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = {
        justify: "flex-end"
      };
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <StyledCardSection muted={true}><Grid>{t17}{t18}{t19}<GridUnit size={0.25}><Div flex={t20}><Text muted={true} semi={true} size="xs" p={{
                x: 4,
                y: 1
              }} style={{
                lineHeight: "20px"
              }}>Cost</Text></Div></GridUnit></Grid></StyledCardSection>;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    t11 = meters_usage.length === 0 ? <StyledCardSection p={3}><Loading /></StyledCardSection> : meters_usage.filter(_temp6).map(_temp9);
    T1 = StyledCardSection;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        justify: "space-between"
      };
      t8 = {
        x: 4,
        y: 3
      };
      t9 = <Text semi={true} size="s">Total metered usage cost</Text>;
      $[41] = t7;
      $[42] = t8;
      $[43] = t9;
    } else {
      t7 = $[41];
      t8 = $[42];
      t9 = $[43];
    }
    T0 = Text;
    t3 = true;
    t4 = "s";
    t5 = "$";
    t6 = meters_usage.reduce(_temp10, 0).toFixed(2);
    $[8] = active_meter_index;
    $[9] = subscription?.max_requests;
    $[10] = subscription_details;
    $[11] = timezone;
    $[12] = usage_data;
    $[13] = T0;
    $[14] = T1;
    $[15] = T2;
    $[16] = T3;
    $[17] = T4;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    T0 = $[13];
    T1 = $[14];
    T2 = $[15];
    T3 = $[16];
    T4 = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t3 = $[25];
    t4 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
  }
  let t17;
  if ($[44] !== T0 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5 || $[48] !== t6) {
    t17 = <T0 semi={t3} size={t4}>{t5}{t6}</T0>;
    $[44] = T0;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t6;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== T1 || $[51] !== t17 || $[52] !== t7 || $[53] !== t8 || $[54] !== t9) {
    t18 = <T1 flex={t7} p={t8}>{t9}{t17}</T1>;
    $[50] = T1;
    $[51] = t17;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== T2 || $[57] !== t10 || $[58] !== t11 || $[59] !== t18) {
    t19 = <T2>{t10}{t11}{t18}</T2>;
    $[56] = T2;
    $[57] = t10;
    $[58] = t11;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== T3 || $[62] !== t12 || $[63] !== t19) {
    t20 = <T3 m={t12}>{t19}</T3>;
    $[61] = T3;
    $[62] = t12;
    $[63] = t19;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== T4 || $[66] !== t13 || $[67] !== t14 || $[68] !== t15 || $[69] !== t16 || $[70] !== t20) {
    t21 = <T4 m={t13}>{t14}{t15}{t16}{t20}</T4>;
    $[65] = T4;
    $[66] = t13;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
    $[70] = t20;
    $[71] = t21;
  } else {
    t21 = $[71];
  }
  return t21;
};
export default EventsUsage;
function _temp(total_usage, tiered_config) {
  const package_size = Number(tiered_config.package_size);
  const tiers = tiered_config.tiers;
  let total_cost = 0;
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    const tier_lower = Number(tier.tier_lower_bound);
    const tier_upper = tiers[i + 1] ? Number(tiers[i + 1].tier_lower_bound) : Infinity;
    const per_unit_price = Number(tier.per_unit);
    if (total_usage <= tier_lower) {
      break;
    }
    const usage_in_tier = Math.min(total_usage, tier_upper) - tier_lower;
    if (usage_in_tier > 0) {
      const packages_in_tier = Math.ceil(usage_in_tier / package_size);
      total_cost = total_cost + packages_in_tier * per_unit_price;
    }
  }
  return total_cost;
}
function _temp2(acc_0, curr) {
  return acc_0 + curr.quantity;
}
function _temp3(v) {
  return v;
}
function _temp4(value) {
  return <NumberFormat renderText={_temp3} displayType="text" value={value} thousandSeparator="," />;
}
function _temp5(meter) {
  return {
    label: meter.display_name,
    key: meter.display_name
  };
}
function _temp6(meter_1) {
  return !!meter_1;
}
function _temp7(v_0) {
  return v_0;
}
function _temp8(v_1) {
  return v_1;
}
function _temp9(meter_2) {
  return <StyledCardSection key={meter_2.display_name}><Grid><GridUnit size={0.25}><Text size="s" p={{
          x: 4,
          y: 3
        }}>{meter_2.display_name}</Text></GridUnit><GridUnit size={0.25}><Text size="s" p={{
          x: 4,
          y: 3
        }}><NumberFormat renderText={_temp7} displayType="text" value={meter_2.total_usage} thousandSeparator="," /></Text></GridUnit><GridUnit size={0.25}><Text size="s" p={{
          x: 4,
          y: 3
        }}><NumberFormat renderText={_temp8} displayType="text" value={meter_2.included} thousandSeparator="," /></Text></GridUnit><GridUnit size={0.25}><Div flex={{
          justify: "flex-end"
        }}><Text size="s" p={{
            x: 4,
            y: 3
          }}>${meter_2.overage.toFixed(2)}</Text></Div></GridUnit></Grid></StyledCardSection>;
}
function _temp10(total, t0) {
  const {
    overage: overage_0
  } = t0;
  return total + overage_0;
}